import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import App from "./App";
import { buildLoginUrl } from "./const";
import { disableBrowserScrollRestoration } from "@/hooks/useScrollToTop";
import "./index.css";

// TALAPP-1925: un "no existe / no es tuya / sin sesión" no se arregla
// reintentando — sin esto la pantalla quedaba varios segundos en spinner antes
// de mostrar "Solicitud no encontrada". El resto de los errores conserva los
// 3 reintentos por defecto de TanStack.
const NON_RETRYABLE_CODES = new Set(["NOT_FOUND", "FORBIDDEN", "UNAUTHORIZED"]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof TRPCClientError && NON_RETRYABLE_CODES.has(error.data?.code)) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  // TALAPP-1862: antes esto mandaba a la pantalla de login de KEYCLOAK.
  // Ahora cae en la pantalla propia, que explica por que se corto la sesion.
  window.location.href = buildLoginUrl("expired");
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated") {
    if (event.action.type === "error") {
      const error = event.query.state.error;
      redirectToLoginIfUnauthorized(error);
      // API Query Error - logged server-side
    }
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated") {
    if (event.action.type === "error") {
      const error = event.mutation.state.error;
      redirectToLoginIfUnauthorized(error);
      // API Mutation Error - logged server-side
    }
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

// TALAPP-1841 — el browser restaura el scroll en F5/back ANTES de que React
// monte, así que el reset por ruta no alcanza a pisarlo: un F5 sobre /review
// scrolleado volvía a abrir al final. Debe correr antes del primer render.
disableBrowserScrollRestoration();

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
